import { useState, useMemo } from 'react'
import { useTheme } from '../context/ThemeContext'
import {
    AreaChart, Area, BarChart, Bar, Cell, LabelList,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts'

function fmtK(n) {
    const abs = Math.abs(n)
    if (abs >= 1000000) return `${(n / 1000000).toFixed(2)}M`
    if (abs >= 10000) return `${(n / 1000).toFixed(1)}K`
    if (abs >= 1000) return `${(n / 1000).toFixed(1)}K`
    return Math.round(n).toLocaleString()
}

function fmtDollar(n) {
    const abs = Math.abs(n)
    if (abs >= 1000000) return `$${(n / 1000000).toFixed(2)}M`
    if (abs >= 1000) return `$${(n / 1000).toFixed(0)}K`
    return `$${Math.round(n).toLocaleString()}`
}

/* Modern progress card: large value + percentage pill + thick progress bar + optional pace marker */
function ProgressCard({ value, max, pace, target, formatValue, color = '#16a34a', isDark = false }) {
    const pct = max > 0 ? (value / max * 100) : 0
    const barPct = Math.min(pct, 100)
    const pacePct = pace != null && max > 0 ? Math.min(pace / max * 100, 100) : null
    const targetPct = target != null && target > 0 ? (value / target * 100) : null

    const textMain = isDark ? 'text-white' : 'text-gray-900'
    const textSub = isDark ? 'text-gray-400' : 'text-gray-500'
    const bgTrack = isDark ? 'bg-slate-700' : 'bg-gray-200'

    return (
        <div className="px-4 py-5 flex flex-col items-center gap-2 w-full">
            <p className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${textMain}`}>
                {formatValue(value)}
            </p>
            {targetPct != null && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: color }}>
                    {targetPct.toFixed(0)}% of goal
                </span>
            )}
            <div className="w-full mt-1 relative">
                <div className={`w-full h-3.5 rounded-full ${bgTrack}`}>
                    <div
                        className="h-3.5 rounded-full"
                        style={{ width: `${barPct}%`, backgroundColor: color, transition: 'width 0.7s ease' }}
                    />
                </div>
                {pacePct != null && (
                    <div className="absolute -top-1.5 flex flex-col items-center"
                        style={{ left: `${Math.min(pacePct, 100)}%`, transform: 'translateX(-50%)' }}>
                        <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-blue-500" />
                        <div className="w-0.5 h-[22px] bg-blue-500" />
                    </div>
                )}
            </div>
            <div className="flex justify-between w-full">
                <span className={`text-[10px] ${textSub}`}>{formatValue(0)}</span>
                <span className={`text-[10px] ${textSub}`}>{formatValue(max)}</span>
            </div>
            {pace != null && (
                <p className={`text-xs ${textSub} flex items-center gap-1`}>
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-sm" />
                    Forecast: {formatValue(pace)}
                </p>
            )}
        </div>
    )
}

function SalesView({ data, loading, selectedMonth }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
        )
    }

    if (!data) {
        return (
            <p className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No sales data available
            </p>
        )
    }

    // --- Raw data from API ---
    const currentWTI = data?.wti?.current || 0
    const avgWTI = data?.wti?.avgMTD || 0
    const grossTarget = data?.revenue?.grossTarget || 0
    const netTarget = data?.revenue?.netTarget || 0
    const currentGross = data?.revenue?.currentGross || 0
    const currentNet = data?.revenue?.currentNet || 0
    const projectedGross = data?.revenue?.projectedGross || 0
    const projectedNet = data?.revenue?.projectedNet || 0
    const dailyAvg = data?.pace?.dailyAvg || 0

    // --- Filter states ---
    const [selectedRoute, setSelectedRoute] = useState(null)
    const [selectedLease, setSelectedLease] = useState(null)

    const handleRouteClick = (route) => {
        setSelectedRoute(prev => {
            if (prev === route) return null
            return route
        })
        setSelectedLease(null) // always clear lease when route changes
    }

    const handleLeaseClick = (leaseName) => {
        setSelectedLease(prev => prev === leaseName ? null : leaseName)
    }

    const clearFilters = () => {
        setSelectedRoute(null)
        setSelectedLease(null)
    }

    // --- Filter label ---
    const filterLabel = selectedLease || selectedRoute || null

    // --- Sales by route for bar chart ---
    const salesByRoute = useMemo(() => {
        if (!data?.salesByRoute) return []
        return data.salesByRoute.map(r => ({
            name: r.route || 'Unassigned',
            value: Math.round(r.salesBBL || 0),
        }))
    }, [data?.salesByRoute])

    // --- Leases filtered by route (for table display) ---
    const filteredLeases = useMemo(() => {
        if (!data?.salesByLease) return []
        if (!selectedRoute) return data.salesByLease
        return data.salesByLease.filter(r => (r.route || 'Unassigned') === selectedRoute)
    }, [data?.salesByLease, selectedRoute])

    const filteredLeaseTotal = useMemo(() => {
        return filteredLeases.reduce((sum, r) => sum + (r.salesBBL || 0), 0)
    }, [filteredLeases])

    // --- Active sales total (respects both route AND lease filter, for KPI cards) ---
    const activeSalesTotal = useMemo(() => {
        if (selectedLease) {
            const match = (data?.salesByLease || []).find(r => r.leaseName === selectedLease)
            return match ? (match.salesBBL || 0) : 0
        }
        return filteredLeaseTotal
    }, [data?.salesByLease, selectedLease, filteredLeaseTotal])

    // --- Forecast data: keyed by comboCurveTie (lease group) ---
    // forecastByLease from API has { comboCurveTie, route, forecastBBL }
    // salesByLease from API has { leaseName, route, comboCurveTie, salesBBL }
    const forecastLookup = useMemo(() => {
        const map = {}
        ;(data?.forecastByLease || []).forEach(f => {
            const key = f.comboCurveTie || ''
            map[key] = (map[key] || 0) + (f.forecastBBL || 0)
        })
        return map
    }, [data?.forecastByLease])

    // Active forecast filtered by route + lease
    const activeForecast = useMemo(() => {
        if (selectedLease) {
            // Find the comboCurveTie for this well and return its forecast
            const match = (data?.salesByLease || []).find(r => r.leaseName === selectedLease)
            const combo = match?.comboCurveTie || ''
            return forecastLookup[combo] || 0
        }
        if (selectedRoute) {
            // Sum forecasts for all combos in this route
            return (data?.forecastByLease || [])
                .filter(f => (f.route || 'Unassigned') === selectedRoute)
                .reduce((sum, f) => sum + (f.forecastBBL || 0), 0)
        }
        // All forecasts
        return (data?.forecastByLease || []).reduce((sum, f) => sum + (f.forecastBBL || 0), 0)
    }, [data?.forecastByLease, data?.salesByLease, forecastLookup, selectedRoute, selectedLease])

    const filteredForecastTotal = useMemo(() => {
        // Sum forecasts for combos represented in filtered leases (dedupe by comboCurveTie)
        const seenCombos = new Set()
        let total = 0
        filteredLeases.forEach(r => {
            const combo = r.comboCurveTie || ''
            if (combo && !seenCombos.has(combo)) {
                seenCombos.add(combo)
                total += forecastLookup[combo] || 0
            }
        })
        return total
    }, [filteredLeases, forecastLookup])

    // --- Tickets filtered by route + lease ---
    const filteredTickets = useMemo(() => {
        if (!data?.tickets) return []
        let tickets = data.tickets
        if (selectedRoute) tickets = tickets.filter(t => (t.route || 'Unassigned') === selectedRoute)
        if (selectedLease) tickets = tickets.filter(t => (t.leaseName || '') === selectedLease)
        return tickets
    }, [data?.tickets, selectedRoute, selectedLease])

    // --- Daily sales (filtered by route/lease, aggregated by date, zero-filled) ---
    const dailySales = useMemo(() => {
        if (!data?.daily) return []
        let rows = data.daily
        if (selectedRoute) rows = rows.filter(d => (d.route || 'Unassigned') === selectedRoute)
        if (selectedLease) rows = rows.filter(d => (d.leaseName || '') === selectedLease)
        const byDate = {}
        rows.forEach(d => {
            if (!byDate[d.date]) byDate[d.date] = { date: d.date, salesBBL: 0 }
            byDate[d.date].salesBBL += Math.round(d.salesBBL || 0)
        })
        // Fill in every day of the selected month so zero days show
        const now = new Date()
        let y, m, lastDay
        if (selectedMonth) {
            const [sy, sm] = selectedMonth.split('-').map(Number)
            y = sy
            m = sm - 1 // JS month is 0-indexed
            const isCurrent = sy === now.getFullYear() && sm === (now.getMonth() + 1)
            lastDay = isCurrent ? now.getDate() : new Date(sy, sm, 0).getDate()
        } else {
            y = now.getFullYear()
            m = now.getMonth()
            lastDay = now.getDate()
        }
        for (let d = 1; d <= lastDay; d++) {
            const dt = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
            if (!byDate[dt]) byDate[dt] = { date: dt, salesBBL: 0 }
        }
        return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date))
    }, [data?.daily, selectedRoute, selectedLease, selectedMonth])

    // --- Filtered daily average (for chart reference line) ---
    const filteredDailyAvg = useMemo(() => {
        if (dailySales.length === 0) return 0
        const total = dailySales.reduce((sum, d) => sum + d.salesBBL, 0)
        return Math.round(total / dailySales.length)
    }, [dailySales])

    // --- Computed KPI values (filter-aware) ---
    const salesVsForecastPct = activeForecast > 0 ? (activeSalesTotal / activeForecast * 100) : 0
    const salesVsForecastDelta = activeSalesTotal - activeForecast

    // Revenue projection - scale proportionally when filtered
    const totalForecast = useMemo(() => {
        return (data?.forecastByLease || []).reduce((sum, f) => sum + (f.forecastBBL || 0), 0)
    }, [data?.forecastByLease])

    // When filtered, scale revenue by the ratio of filtered forecast to total forecast
    const forecastRatio = totalForecast > 0 ? (activeForecast / totalForecast) : 1
    const isFiltered = selectedRoute || selectedLease
    const displayCurrentGross = isFiltered ? Math.round(currentGross * forecastRatio) : currentGross
    const displayCurrentNet = isFiltered ? Math.round(currentNet * forecastRatio) : currentNet
    const displayGross = isFiltered ? Math.round(projectedGross * forecastRatio) : projectedGross
    const displayNet = isFiltered ? Math.round(projectedNet * forecastRatio) : projectedNet
    const displayGrossTarget = isFiltered ? Math.round(grossTarget * forecastRatio) : grossTarget
    const displayNetTarget = isFiltered ? Math.round(netTarget * forecastRatio) : netTarget

    // --- Pace vs Goal: daily pacing against forecast ---
    // "By day X we should be at Y based on forecast; we've sold Z, so we're +/- the goal"
    const daysInMonth = data?.kpis?.daysInMonth || 30
    const daysElapsed = data?.kpis?.daysWithData || 1
    const dailyForecastGoal = activeForecast / daysInMonth
    const expectedPaceToDate = Math.round(dailyForecastGoal * daysElapsed)
    const paceVsGoal = activeSalesTotal - expectedPaceToDate
    const paceVsGoalPct = expectedPaceToDate > 0 ? (paceVsGoal / expectedPaceToDate * 100) : 0

    // Gauge max values
    const salesGaugeMax = activeForecast > 0 ? activeForecast : Math.ceil(activeSalesTotal * 1.2 / 5000) * 5000 || 30000
    const grossGaugeMax = displayGrossTarget > 0 ? displayGrossTarget : Math.ceil(displayGross * 1.2 / 100000) * 100000 || 3000000
    const netGaugeMax = displayNetTarget > 0 ? displayNetTarget : Math.ceil(displayNet * 1.2 / 100000) * 100000 || 1500000

    // --- KPI card definitions (update with filters) ---
    const kpiDefs = [
        { label: 'Month Forecast', value: fmtK(activeForecast) },
        { label: 'MTD Sales', value: fmtK(activeSalesTotal) },
        { label: 'vs Forecast', value: `${salesVsForecastPct.toFixed(0)}%`, highlight: salesVsForecastPct >= 100 ? 'bg-green-600' : salesVsForecastPct >= 80 ? 'bg-green-700' : 'bg-amber-600' },
        { label: 'WTI Current', value: `$${currentWTI.toFixed(2)}` },
        { label: 'WTI Avg MTD', value: `$${avgWTI.toFixed(2)}` },
        { label: 'MTD Tickets', value: filteredTickets.length.toLocaleString() },
    ]

    const paceColor = paceVsGoal >= 0 ? 'bg-green-600' : 'bg-red-600'
    const paceDefs = [
        { label: `Forecast Pace (Day ${daysElapsed})`, value: fmtK(expectedPaceToDate), highlight: 'bg-green-700' },
        { label: 'vs Goal', value: `${paceVsGoal >= 0 ? '+' : ''}${fmtK(paceVsGoal)}`, highlight: paceColor },
        { label: 'vs Goal %', value: `${paceVsGoalPct >= 0 ? '+' : ''}${paceVsGoalPct.toFixed(0)}%`, highlight: paceColor },
    ]

    function getDaysSinceColor(days) {
        if (days > 7) return isDark ? 'text-red-400' : 'text-red-600'
        if (days > 3) return isDark ? 'text-amber-400' : 'text-amber-600'
        return isDark ? 'text-green-400' : 'text-green-600'
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Filter indicator */}
            {isFiltered && (
                <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg ${isDark ? 'bg-slate-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    <span>Filtered:</span>
                    {selectedRoute && (
                        <span className="px-2 py-0.5 rounded bg-green-700 text-white font-medium">{selectedRoute}</span>
                    )}
                    {selectedLease && (
                        <span className="px-2 py-0.5 rounded bg-blue-600 text-white font-medium">{selectedLease}</span>
                    )}
                    <button onClick={clearFilters} className="ml-auto underline hover:text-white">Clear All</button>
                </div>
            )}

            {/* Row 1: Green KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {kpiDefs.map((kpi, i) => (
                    <div key={i} className={`${kpi.highlight || 'bg-green-700'} rounded-lg p-3 sm:p-4 text-center`}>
                        <p className="text-white/80 text-[10px] sm:text-xs font-medium">{kpi.label}</p>
                        <p className="text-white text-xl sm:text-3xl font-bold mt-1">{kpi.value}</p>
                    </div>
                ))}
            </div>

            {/* Row 1b: Pace KPI Cards */}
            <div className="grid grid-cols-3 gap-3">
                {paceDefs.map((kpi, i) => (
                    <div key={i} className={`${kpi.highlight} rounded-lg p-3 sm:p-4 text-center`}>
                        <p className="text-white/80 text-[10px] sm:text-xs font-medium">{kpi.label}</p>
                        <p className="text-white text-xl sm:text-3xl font-bold mt-1">{kpi.value}</p>
                    </div>
                ))}
            </div>

            {/* Row 2: Sales to Date Gauge + Daily Oil Sales Chart */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                {/* Sales to Date Gauge */}
                <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                        <h3 className="text-sm font-semibold text-white">
                            Sales to Date
                            {filterLabel && <span className="ml-1 text-white/70 text-xs font-normal">({filterLabel})</span>}
                        </h3>
                    </div>
                    <div className="p-4 flex justify-center">
                        <ProgressCard
                            value={activeSalesTotal}
                            max={salesGaugeMax}
                            pace={activeForecast > 0 ? activeForecast : null}
                            formatValue={fmtK}
                            isDark={isDark}
                        />
                    </div>
                </div>

                {/* Daily Oil Sales Area Chart */}
                <div className={`xl:col-span-2 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                        <h3 className="text-sm font-semibold text-white">
                            Daily Oil Sales
                            {filterLabel && <span className="ml-1 text-white/70 text-xs font-normal">({filterLabel})</span>}
                        </h3>
                    </div>
                    <div className="p-4">
                        {dailySales.length > 0 && (
                            <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={dailySales}>
                                    <defs>
                                        <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#16a34a" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#16a34a" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                                        tickFormatter={d => {
                                            const dt = new Date(d + 'T00:00:00')
                                            return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate().toString().padStart(2, '0')}`
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                                        tickFormatter={v => fmtK(v)}
                                        label={{ value: 'Oil Sales', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' } }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                        }}
                                        formatter={(v) => [`${v?.toLocaleString()} BBL`, 'Oil Sales']}
                                        labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                                    />
                                    {filteredDailyAvg > 0 && (
                                        <ReferenceLine
                                            y={filteredDailyAvg}
                                            stroke="#16a34a"
                                            strokeDasharray="6 4"
                                            strokeWidth={1.5}
                                            label={{ value: `Avg: ${fmtK(filteredDailyAvg)}`, position: 'insideTopRight', style: { fontSize: 10, fill: '#16a34a' } }}
                                        />
                                    )}
                                    <Area
                                        type="monotone"
                                        dataKey="salesBBL"
                                        stroke="#16a34a"
                                        strokeWidth={2}
                                        fill="url(#salesGradient)"
                                    >
                                        <LabelList
                                            dataKey="salesBBL"
                                            position="top"
                                            style={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#374151' }}
                                            formatter={v => fmtK(v)}
                                        />
                                    </Area>
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 3: Revenue Gauges + Oil Sales by Lease */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                {/* Revenue Gauges (left 2/3) - 2x2 grid: current + projected */}
                <div className="xl:col-span-2 grid grid-cols-2 gap-4">
                    {/* Current Gross Revenue (MTD actual) */}
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">
                                Gross Revenue MTD
                                {isFiltered && <span className="ml-1 text-white/70 text-xs font-normal">(est.)</span>}
                            </h3>
                        </div>
                        <div className="p-4 flex justify-center">
                            <ProgressCard
                                value={displayCurrentGross}
                                max={displayGrossTarget > 0 ? displayGrossTarget : Math.ceil(displayCurrentGross * 1.2 / 100000) * 100000 || 3000000}
                                target={displayGrossTarget > 0 ? displayGrossTarget : null}
                                formatValue={fmtDollar}
                                isDark={isDark}
                            />
                        </div>
                    </div>

                    {/* Current Net Revenue (MTD actual) */}
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">
                                Net Revenue MTD
                                {isFiltered && <span className="ml-1 text-white/70 text-xs font-normal">(est.)</span>}
                            </h3>
                        </div>
                        <div className="p-4 flex justify-center">
                            <ProgressCard
                                value={displayCurrentNet}
                                max={displayNetTarget > 0 ? displayNetTarget : Math.ceil(displayCurrentNet * 1.2 / 100000) * 100000 || 1500000}
                                target={displayNetTarget > 0 ? displayNetTarget : null}
                                formatValue={fmtDollar}
                                isDark={isDark}
                            />
                        </div>
                    </div>

                    {/* Projected Gross Revenue (extrapolated to full month) */}
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">
                                Projected Gross Revenue
                                {isFiltered && <span className="ml-1 text-white/70 text-xs font-normal">(est.)</span>}
                            </h3>
                        </div>
                        <div className="p-4 flex justify-center">
                            <ProgressCard
                                value={displayGross}
                                max={grossGaugeMax}
                                target={displayGrossTarget > 0 ? displayGrossTarget : null}
                                formatValue={fmtDollar}
                                isDark={isDark}
                            />
                        </div>
                    </div>

                    {/* Projected Net Revenue (extrapolated to full month) */}
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">
                                Projected Net Revenue
                                {isFiltered && <span className="ml-1 text-white/70 text-xs font-normal">(est.)</span>}
                            </h3>
                        </div>
                        <div className="p-4 flex justify-center">
                            <ProgressCard
                                value={displayNet}
                                max={netGaugeMax}
                                target={displayNetTarget > 0 ? displayNetTarget : null}
                                formatValue={fmtDollar}
                                isDark={isDark}
                            />
                        </div>
                    </div>
                </div>

                {/* Oil Sales by Lease Table (right 1/3) - CLICKABLE rows */}
                <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="px-4 py-2 bg-green-700 rounded-t-lg flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-white">
                            Oil Sales by Lease
                            {selectedRoute && !selectedLease && <span className="ml-1 text-white/70 text-xs font-normal">({selectedRoute})</span>}
                            {selectedLease && <span className="ml-1 text-white/70 text-xs font-normal">({selectedLease})</span>}
                        </h3>
                        {(selectedRoute || selectedLease) && (
                            <button onClick={clearFilters} className="text-white/80 hover:text-white text-xs underline">
                                Clear
                            </button>
                        )}
                    </div>
                    <div className="max-h-[340px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                                    <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Lease</th>
                                    <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sales</th>
                                    <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Forecast</th>
                                    <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeases.map((row, i) => {
                                    const fc = forecastLookup[row.comboCurveTie || ''] || 0
                                    const pctOfFc = fc > 0 ? ((row.salesBBL || 0) / fc * 100) : 0
                                    const isSelected = selectedLease === row.leaseName
                                    return (
                                        <tr
                                            key={row.leaseName || i}
                                            onClick={() => handleLeaseClick(row.leaseName)}
                                            className={`cursor-pointer transition-colors ${
                                                isSelected
                                                    ? (isDark ? 'bg-blue-900/40 border-l-2 border-blue-500' : 'bg-blue-50 border-l-2 border-blue-500')
                                                    : i % 2 === 0
                                                        ? (isDark ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-gray-50')
                                                        : (isDark ? 'bg-slate-800/50 hover:bg-slate-800' : 'bg-gray-50/50 hover:bg-gray-100')
                                            }`}
                                        >
                                            <td className={`px-3 py-1 ${isSelected ? (isDark ? 'text-blue-300 font-semibold' : 'text-blue-700 font-semibold') : (isDark ? 'text-gray-300' : 'text-gray-700')}`}>
                                                {row.leaseName || 'Unknown'}
                                            </td>
                                            <td className={`px-3 py-1 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {(row.salesBBL || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </td>
                                            <td className={`px-3 py-1 text-right ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {fc > 0 ? fmtK(fc) : '-'}
                                            </td>
                                            <td className={`px-3 py-1 text-right font-medium ${
                                                fc > 0
                                                    ? pctOfFc >= 100 ? (isDark ? 'text-green-400' : 'text-green-600')
                                                        : pctOfFc >= 70 ? (isDark ? 'text-amber-400' : 'text-amber-600')
                                                        : (isDark ? 'text-red-400' : 'text-red-600')
                                                    : (isDark ? 'text-gray-600' : 'text-gray-300')
                                            }`}>
                                                {fc > 0 ? `${pctOfFc.toFixed(0)}%` : '-'}
                                            </td>
                                        </tr>
                                    )
                                })}
                                {/* Total row */}
                                <tr className="bg-green-700">
                                    <td className="px-3 py-1.5 text-white font-bold">Total</td>
                                    <td className="px-3 py-1.5 text-right text-white font-bold">
                                        {filteredLeaseTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-white/80 font-medium">
                                        {filteredForecastTotal > 0 ? fmtK(filteredForecastTotal) : '-'}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-white font-bold">
                                        {filteredForecastTotal > 0 ? `${(filteredLeaseTotal / filteredForecastTotal * 100).toFixed(0)}%` : '-'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Row 4: Sales by Route - clickable horizontal bar chart */}
            {salesByRoute.length > 0 && (
                <div className={`rounded-lg border p-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="mb-3 pb-1 border-b-2 border-green-600 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-green-600">Sales by Route</h3>
                        {selectedRoute && (
                            <button onClick={() => { setSelectedRoute(null); setSelectedLease(null) }}
                                className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                                Clear: {selectedRoute}
                            </button>
                        )}
                    </div>
                    <p className={`text-[10px] mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Click a route to filter, then click a lease to drill down</p>
                    <ResponsiveContainer width="100%" height={salesByRoute.length * 28 + 20}>
                        <BarChart layout="vertical" data={salesByRoute} margin={{ left: 0, right: 60, top: 0, bottom: 0 }}
                            onClick={(e) => {
                                const route = e?.activePayload?.[0]?.payload?.name
                                if (route) handleRouteClick(route)
                            }}
                            style={{ cursor: 'pointer' }}>
                            <XAxis type="number" hide />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={140}
                                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#4b5563', fontWeight: 500 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e293b' : '#fff',
                                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                }}
                                formatter={(v) => [`${v.toLocaleString()} BBL`, 'Oil Sales']}
                            />
                            <Bar dataKey="value" barSize={20} radius={[0, 4, 4, 0]}>
                                {salesByRoute.map((entry) => (
                                    <Cell
                                        key={entry.name}
                                        fill={selectedRoute === entry.name ? '#15803d' : (selectedRoute ? (isDark ? '#334155' : '#d1d5db') : '#16a34a')}
                                        stroke={selectedRoute === entry.name ? '#166534' : 'none'}
                                        strokeWidth={selectedRoute === entry.name ? 2 : 0}
                                    />
                                ))}
                                <LabelList
                                    dataKey="value"
                                    position="right"
                                    style={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#374151', fontWeight: 600 }}
                                    formatter={v => v.toLocaleString()}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Row 5: Recent Tickets Table + Purchaser Performance side by side */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Recent Tickets */}
                {filteredTickets.length > 0 && (
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">
                                Recent Run Tickets
                                {filterLabel && <span className="ml-1 text-white/70 text-xs font-normal">({filterLabel})</span>}
                            </h3>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                                        <th className={`px-2 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</th>
                                        <th className={`px-2 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Well</th>
                                        <th className={`px-2 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Purchaser</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gross</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Net</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTickets.map((ticket, i) => (
                                        <tr
                                            key={ticket.ticketNumber || i}
                                            className={i % 2 === 0
                                                ? (isDark ? 'bg-slate-900' : 'bg-white')
                                                : (isDark ? 'bg-slate-800/50' : 'bg-gray-50/50')
                                            }
                                        >
                                            <td className={`px-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {ticket.ticketDate ? new Date(ticket.ticketDate + 'T00:00:00').toLocaleDateString() : '-'}
                                            </td>
                                            <td className={`px-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {ticket.wellName || '-'}
                                            </td>
                                            <td className={`px-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {ticket.purchaserName || '-'}
                                            </td>
                                            <td className={`px-2 py-1 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {ticket.grossVolume?.toLocaleString(undefined, { maximumFractionDigits: 1 }) || '-'}
                                            </td>
                                            <td className={`px-2 py-1 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {ticket.netVolume?.toLocaleString(undefined, { maximumFractionDigits: 1 }) || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Purchaser Performance */}
                {data.purchasers && data.purchasers.length > 0 && (
                    <div className={`rounded-lg border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="px-4 py-2 bg-green-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">Purchaser Performance</h3>
                        </div>
                        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                                        <th className={`px-2 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Purchaser</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tickets</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total BBL</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg BBL</th>
                                        <th className={`px-2 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Days Since</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.purchasers.map((p, i) => (
                                        <tr
                                            key={p.purchaserName || i}
                                            className={i % 2 === 0
                                                ? (isDark ? 'bg-slate-900' : 'bg-white')
                                                : (isDark ? 'bg-slate-800/50' : 'bg-gray-50/50')
                                            }
                                        >
                                            <td className={`px-2 py-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {p.purchaserName || 'Unknown'}
                                            </td>
                                            <td className={`px-2 py-1 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {p.ticketCount?.toLocaleString() || '-'}
                                            </td>
                                            <td className={`px-2 py-1 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {p.totalVolumeBBL?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            </td>
                                            <td className={`px-2 py-1 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {p.avgTicketVolume?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            </td>
                                            <td className={`px-2 py-1 text-right font-medium ${p.daysSinceLastPickup != null ? getDaysSinceColor(p.daysSinceLastPickup) : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                                                {p.daysSinceLastPickup != null ? p.daysSinceLastPickup : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SalesView
